// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnComplete,
    HasOnSuccess,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { GoogleAddressValidationResponse, validateAddress } from "@west/App/Services/GoogleApiService";

export interface AddressValidationParameter {
    address: BillToModel | ShipToModel;
}

export type AddressValidationHandlerType = ApiHandler<
    AddressValidationParameter & HasOnSuccess<boolean>,
    GoogleAddressValidationResponse,
    { doesAddressMatch: boolean }
>;

export const CallGoogleApi: AddressValidationHandlerType = async props => {
    const address = props.parameter.address;
    const addressLines: string[] = [address.address1, address.address2, address.address3, address.address4].reduce(
        (result, address) => {
            if (!address) {
                return result;
            }
            const trimmed = address.trim();
            if (trimmed.length > 0) {
                result.push(trimmed);
            }
            return result;
        },
        [] as string[],
    );

    props.apiResult = await validateAddress(
        {
            address: {
                addressLines,
                locality: address.city,
                administrativeArea: address.state?.abbreviation,
                postalCode: address.postalCode,
                regionCode: address.country?.abbreviation,
            },
        },
        getSettingsCollection(props.getState()).websiteSettings.googleMapsApiKey,
    );
};

export const CheckPostalCode: AddressValidationHandlerType = props => {
    props.doesAddressMatch = false;
    const userAddress = props.parameter.address;
    const googleAddress = props.apiResult.result.address.postalAddress;
    const unconfirmedunconfirmedComponentTypes = props.apiResult.result.address.unconfirmedComponentTypes;

    // return error if postalcode, state or city are unconfirmed
    if (
        (userAddress.state && userAddress.state.abbreviation !== googleAddress.administrativeArea) ||
        (unconfirmedunconfirmedComponentTypes && unconfirmedunconfirmedComponentTypes.includes("postal_code")) ||
        (unconfirmedunconfirmedComponentTypes &&
            unconfirmedunconfirmedComponentTypes.includes("administrative_area_level_1")) ||
        (unconfirmedunconfirmedComponentTypes && unconfirmedunconfirmedComponentTypes.includes("locality"))
    ) {
        return;
    }

    if (googleAddress?.postalCode) {
        const [googlePostal] = googleAddress.postalCode.split("-");
        const [userPostal] = userAddress.postalCode.split("-");

        if (googlePostal !== userPostal) {
            return;
        }
    }

    if (googleAddress?.locality && googleAddress.locality !== userAddress.city) {
        return;
    }
    props.doesAddressMatch = true;
};

export const ExecuteOnSuccessCallback: AddressValidationHandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.(props.doesAddressMatch);
};

const addressValidation = createHandlerChainRunner(
    [CallGoogleApi, CheckPostalCode, ExecuteOnSuccessCallback],
    "AddressValidation",
);
export default addressValidation;
